import {
  type Client,
  type Cohort,
  type Contact,
  DataTypeSystem,
  type UserGroup,
} from '@efdigital/ll-addon-data';
import {
  setAsyncBelongsTo,
  setAsyncHasMany,
} from '@efdigital/ll-admin-v2/data/async-setter';
import { deepSave } from '@efdigital/ll-admin-v2/data/deep-save';
import {
  dummyUserAdmin,
  dummyUserParticipant,
} from '@efdigital/ll-admin-v2/utils/user-groups';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';

import {
  EditRecord,
  type EditRecordSignature,
  initialState,
} from '../edit-record.gts';
import ImagePickerBig from '../pickers/image-picker-big.gts';
import { Validation, type ValidationFailure } from '../validation.gts';

export class EditCohort extends Component<EditRecordSignature<Cohort>> {
  @service declare store: Store;

  get startsDate() {
    if (!this.args.item.starts) {
      return '';
    }
    return this.args.item.starts.split('T')[0];
  }

  get endsDate() {
    if (!this.args.item.ends) {
      return '';
    }
    return this.args.item.ends.split('T')[0];
  }

  setTitle = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.item.title = target.value;
  };

  handleClientSelect = (client: Client | null) => {
    setAsyncBelongsTo(this.args.item, 'client', client);
  };

  handleContactSelect = async (contact: Contact) => {
    const contacts = await this.args.item.contacts;
    if (!contacts.find((item) => item.id === contact.id)) {
      setAsyncHasMany(this.args.item, 'contacts', [...contacts, contact]);
    }
  };

  setAbstract = (event: Event) => {
    const target = event.target as HTMLTextAreaElement;
    this.args.item.abstract = target.value;
  };

  setIcon = (icon: string) => {
    this.args.item.icon = icon;
  };

  setStarts = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.item.starts = target.value;
  };

  setEnds = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.item.ends = target.value;
  };

  validate = (item: Cohort) => {
    const failures: ValidationFailure[] = [];
    if (!item.icon) {
      failures.push({
        message: 'Icon is required',
        attribute: 'icon',
      });
    }
    if (!item.starts) {
      failures.push({
        message: 'Start date is required',
        attribute: 'starts',
      });
    }
    if (!item.ends) {
      failures.push({
        message: 'End date is required',
        attribute: 'ends',
      });
    }
    if (item.starts > item.ends) {
      failures.push({
        message: 'Start date must be before end date',
        attribute: 'starts',
      });
    }
    return Validation.fromFailures(failures);
  };
  save = async () => {
    const group = this.store.createRecord<UserGroup>(DataTypeSystem.UserGroup, {
      title: `${this.args.item.slug}-participants`,
      userIds: [dummyUserParticipant],
    });
    await deepSave(group, this.store);
    setAsyncBelongsTo(this.args.item, 'group', group);

    const admingroup = this.store.createRecord<UserGroup>(
      DataTypeSystem.UserGroup,
      {
        title: `${this.args.item.slug}-admins`,
        userIds: [dummyUserAdmin],
      }
    );
    await deepSave(admingroup, this.store);
    setAsyncBelongsTo(this.args.item, 'admingroup', admingroup);

    return deepSave(this.args.item, this.store);
  };
  <template>
    <EditRecord
      @editLabel="Edit Cohort"
      @item={{@item}}
      @context={{@context}}
      @buttonSize="btn-md"
      @initialState={{initialState @item}}
      @validate={{this.validate}}
      @save={{this.save}}
      ...attributes
    >
      <:form as |uniqueId isReadOnly|>
        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow">Listing</legend>

          <div class="mb-3">
            <label
              for="{{uniqueId}}-title"
              class="form-control-label required"
            >Title</label>
            <div>
              <input
                name="title"
                type="text"
                class="form-control"
                id="{{uniqueId}}-title"
                value={{@item.title}}
                required
                {{on "change" this.setTitle}}
              />
            </div>
          </div>

          <div class="mb-3">
            <label
              for="{{uniqueId}}-abstract"
              class="form-control-label required"
            >Abstract</label>
            <div>
              <textarea
                name="abstract"
                class="form-control"
                id="{{uniqueId}}-abstract"
                value={{@item.abstract}}
                required
                {{on "change" this.setAbstract}}
              ></textarea>
            </div>
          </div>

          <div class="mb-3">
            <label
              for="{{uniqueId}}-icon"
              class="form-control-label required"
            >Icon</label>
            <div>
              <ImagePickerBig
                @value={{@item.icon}}
                @onChange={{this.setIcon}}
                @sizeHint="cohort-icon"
                @defaultTitle={{@item.title}}
                @readonly={{isReadOnly}}
                @id="{{uniqueId}}-icon"
                @placeholder="https://"
              />
            </div>
          </div>
        </fieldset>

        <fieldset disabled={{isReadOnly}} class="mb-4">
          <legend class="gud-eyebrow">Dates</legend>

          <div class="row">
            <div class="col-md">
              <div class="mb-3">
                <label
                  for="{{uniqueId}}-starts"
                  class="form-control-label required"
                >Cohort start date</label>
                <div>
                  <input
                    name="starts"
                    type="date"
                    class="form-control"
                    id="{{uniqueId}}-starts"
                    value={{this.startsDate}}
                    required
                    {{on "change" this.setStarts}}
                  />
                </div>
              </div>
            </div>
            <div class="col-md">
              <div class="mb-3">
                <label
                  for="{{uniqueId}}-ends"
                  class="form-control-label required"
                >Cohort end date</label>
                <div>
                  <input
                    name="ends"
                    type="date"
                    class="form-control"
                    id="{{uniqueId}}-ends"
                    value={{this.endsDate}}
                    required
                    {{on "change" this.setEnds}}
                  />
                </div>
              </div>
            </div>
          </div>
        </fieldset>
      </:form>
    </EditRecord>
  </template>
}
